// Keep filters + sort bar stuck flush under the visible header while products scroll.
class MerooCollectionLayout extends HTMLElement {
  connectedCallback() {
    this.sidebar = this.querySelector('.meroo-collection__sidebar');
    this.sortBar = this.querySelector('.meroo-collection__sort-bar');

    this.updateStickyOffset = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        // Measure the real header bar only — not the section/drawer wrapper.
        const headerEl = document.getElementById('MerooHeader') || document.querySelector('.meroo-header');
        let stickyTop = 0;
        if (headerEl) {
          const rect = headerEl.getBoundingClientRect();
          // Follow the header's visible bottom. If it has scrolled away, sit at the top.
          stickyTop = Math.max(0, Math.ceil(rect.bottom));
          // Ignore absurd values from mis-measured wrappers.
          stickyTop = Math.min(stickyTop, Math.ceil(headerEl.offsetHeight) || 68);
        } else {
          stickyTop = 68;
        }

        document.documentElement.style.setProperty('--header-height', `${stickyTop || 68}px`);
        this.style.setProperty('--meroo-sticky-top', `${stickyTop}px`);
        if (this.sortBar) this.sortBar.style.top = `${stickyTop}px`;
        if (this.sidebar) this.sidebar.style.top = `${stickyTop}px`;
      });
    };

    window.addEventListener('resize', this.updateStickyOffset, { passive: true });
    window.addEventListener('scroll', this.updateStickyOffset, { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.updateStickyOffset());
    const headerEl = document.getElementById('MerooHeader') || document.querySelector('.meroo-header');
    if (headerEl) this.resizeObserver.observe(headerEl);
    this.updateStickyOffset();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.updateStickyOffset);
    window.removeEventListener('scroll', this.updateStickyOffset);
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame);
  }
}

if (!customElements.get('meroo-collection-layout')) {
  customElements.define('meroo-collection-layout', MerooCollectionLayout);
}

// Enhance the real form select, so Shopify filtering and no-JS sorting keep
// their existing values while the visible menu uses storefront styling.
class MerooSort extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    if (!this.select) return;

    if (!this.trigger) this.buildMenu();
    this.events = new AbortController();
    const options = { signal: this.events.signal };

    this.trigger.addEventListener('click', () => this.setOpen(this.list.hidden), options);
    this.trigger.addEventListener('keydown', (event) => this.onKeyDown(event), options);
    this.list.addEventListener('pointerdown', (event) => event.preventDefault(), options);
    this.list.addEventListener('click', (event) => {
      const item = event.target.closest('[role="option"]');
      if (item) this.choose(Number(item.dataset.index));
    }, options);
    this.list.addEventListener('pointermove', (event) => {
      const item = event.target.closest('[role="option"]');
      if (item) this.highlight(Number(item.dataset.index), false);
    }, options);
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.setOpen(false);
    }, options);
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.setOpen(false);
    }, options);
    window.addEventListener('resize', () => this.setOpen(false), options);
    this.select.addEventListener('change', () => this.syncSelection(), options);
    this.syncSelection();
  }

  buildMenu() {
    this.trigger = document.createElement('button');
    this.trigger.type = 'button';
    this.trigger.id = `${this.select.id}-trigger`;
    this.trigger.className = 'meroo-sort__trigger';
    this.trigger.setAttribute('data-meroo-sort-trigger', '');
    this.trigger.setAttribute('role', 'combobox');
    this.trigger.setAttribute('aria-haspopup', 'listbox');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-labelledby', `${this.getAttribute('aria-labelledby')} ${this.trigger.id}`);

    this.list = document.createElement('ul');
    this.list.id = `${this.select.id}-options`;
    this.list.className = 'meroo-sort__options';
    this.list.setAttribute('role', 'listbox');
    this.list.setAttribute('aria-labelledby', this.getAttribute('aria-labelledby'));
    this.list.hidden = true;
    this.trigger.setAttribute('aria-controls', this.list.id);
    this.items = Array.from(this.select.options, (option, index) => {
      const item = document.createElement('li');
      item.id = `${this.select.id}-option-${index}`;
      item.className = 'meroo-sort__option';
      item.setAttribute('role', 'option');
      item.dataset.index = index;
      item.textContent = option.textContent;
      this.list.append(item);
      return item;
    });

    this.select.tabIndex = -1;
    this.select.setAttribute('aria-hidden', 'true');
    const label = document.getElementById(this.getAttribute('aria-labelledby'));
    if (label) label.htmlFor = this.trigger.id;
    this.append(this.trigger, this.list);
    this.classList.add('is-enhanced');
  }

  syncSelection() {
    this.trigger.textContent = this.select.selectedOptions[0].textContent;
    this.items.forEach((item, index) => item.setAttribute('aria-selected', String(index === this.select.selectedIndex)));
  }

  setOpen(open) {
    if (open) {
      const bounds = this.trigger.getBoundingClientRect();
      const below = window.innerHeight - bounds.bottom - 16;
      const above = bounds.top - 16;
      const openAbove = below < 220 && above > below;
      this.dataset.placement = openAbove ? 'above' : 'below';
      this.style.setProperty('--meroo-sort-space', `${Math.max(80, openAbove ? above : below)}px`);
    }
    this.list.hidden = !open;
    this.trigger.setAttribute('aria-expanded', String(open));
    if (open) this.highlight(this.select.selectedIndex);
    else this.trigger.removeAttribute('aria-activedescendant');
  }

  highlight(index, scroll = true) {
    this.activeIndex = (index + this.items.length) % this.items.length;
    this.items.forEach((item, itemIndex) => item.classList.toggle('is-highlighted', itemIndex === this.activeIndex));
    const activeItem = this.items[this.activeIndex];
    this.trigger.setAttribute('aria-activedescendant', activeItem.id);
    if (scroll) activeItem.scrollIntoView({ block: 'nearest' });
  }

  choose(index) {
    const changed = this.select.selectedIndex !== index;
    this.select.selectedIndex = index;
    this.syncSelection();
    this.setOpen(false);
    this.trigger.focus({ preventScroll: true });
    if (changed) {
      this.select.dispatchEvent(new Event('input', { bubbles: true }));
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  onKeyDown(event) {
    if (event.key === 'Tab') return this.setOpen(false);
    if (event.key === 'Escape') {
      if (!this.list.hidden) event.preventDefault();
      this.setOpen(false);
      return;
    }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      const wasOpen = !this.list.hidden;
      if (!wasOpen) this.setOpen(true);
      if (event.key === 'Home') this.highlight(0);
      else if (event.key === 'End') this.highlight(this.items.length - 1);
      else if (wasOpen && event.key === 'ArrowDown') this.highlight(this.activeIndex + 1);
      else if (wasOpen && event.key === 'ArrowUp') this.highlight(this.activeIndex - 1);
      else if (wasOpen && ['Enter', ' '].includes(event.key)) this.choose(this.activeIndex);
      return;
    }
    if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      event.preventDefault();
      if (this.list.hidden) this.setOpen(true);
      clearTimeout(this.searchTimer);
      this.search = (this.search || '') + event.key.toLowerCase();
      const index = this.items.findIndex((item) => item.textContent.trim().toLowerCase().startsWith(this.search));
      if (index >= 0) this.highlight(index);
      this.searchTimer = setTimeout(() => { this.search = ''; }, 700);
    }
  }

  disconnectedCallback() {
    this.events?.abort();
    clearTimeout(this.searchTimer);
  }
}

if (!customElements.get('meroo-sort')) {
  customElements.define('meroo-sort', MerooSort);
}
